import { RequestHeaders, APIFetchStudio } from "@certa/network";
import type {
  ObjectMetaData,
  ObjectMetaDataResponse,
  ObjectSchemaRaw,
  ObjectTypeRaw,
  SDODeploymentPayloadResponse,
  SDOObjectLock,
  SDOObjectLockResponse,
  SDOSavePointStatusResponse
} from "../types/studio.types";
import { ObjectStage, ObjectStageRaw } from "../types/studio.types";
import type { PaginatedResultsResponse } from "../types/generic.types";
import type { HashMap } from "@certa/types";
import {
  objectSchemasModelCreator,
  sdoDeploymentModelCreator,
  sdoObjectLockModelCreator,
  sdoSavePointStatusesModelCreator,
  studioDataObjectModelCreator,
  studioDataObjectsModelCreator
} from "../models/studio.models";
import type { StudioObject } from "@certa/studio-core";

const ObjectStageToRaw = {
  [ObjectStage.DRAFT]: ObjectStageRaw.DRAFT,
  [ObjectStage.RFT]: ObjectStageRaw.RFT,
  [ObjectStage.DEPLOYED]: ObjectStageRaw.DEPLOYED,
  [ObjectStage.ARCHIVE]: ObjectStageRaw.ARCHIVE
};

export type ObjectVersion = {
  id: number;
  stage: ObjectStage;
  createdAt: string;
  lastDeployedAt: string | null;
};

// Studio Data Object CRUD
export const getStudioDataObjectsList = ({
  offset = 0,
  stage = ObjectStage.DRAFT
}: {
  offset?: number;
  stage?: ObjectStage;
}) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  const stageRaw = ObjectStageToRaw[stage];

  const url = `objects/?stage=${stageRaw}&limit=200&offset=${offset}`;
  return APIFetchStudio<PaginatedResultsResponse<ObjectMetaDataResponse>>(
    url,
    requestOptions
  ).then(studioDataObjectsModelCreator);
};

export const createStudioDataObject = (payload: {
  name: string;
  description?: string;
}): Promise<ObjectMetaData> => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify(payload)
  };

  return APIFetchStudio<ObjectMetaDataResponse>(
    "objects/",
    requestOptions
  ).then(studioDataObjectModelCreator);
};

export const deleteStudioDataObject = (params: { id: string }) => {
  const { id } = params;

  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio(`objects/${id}/`, requestOptions);
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function getStudioDataObject(id: number) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  return APIFetchStudio<ObjectMetaData>(`objects/${id}/`, requestOptions);
}

// Studio Data Object MetaJSON Get/Update
export function updateStudioDataObjectSchema(props: {
  id: number;
  metaJSON: StudioObject;
  deployableJSON: ObjectTypeRaw;
}) {
  const { id, metaJSON, deployableJSON } = props;
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    body: JSON.stringify({
      meta_json: metaJSON,
      deployable_json: deployableJSON
    })
  };

  return APIFetchStudio<HashMap>(
    `objects/${id}/update_meta_json/`,
    requestOptions
  );
}

export function getStudioDataObjectSchemas({ ids }: { ids: number[] }) {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };

  if (ids.length === 0) {
    return Promise.resolve([]);
  }

  return APIFetchStudio<ObjectSchemaRaw[]>(
    `objects/get_meta_json/?id__in=${ids.join(",")}`,
    requestOptions
  ).then(objectSchemasModelCreator);
}

// Studio SDO Versions or SDO Deployment Logs
export const getStudioDataObjectsVersionsList = ({
  offset = 0,
  stages
}: {
  offset?: number;
  stages?: ObjectStage[];
}) => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.GET,
    credentials: "include"
  };
  const baseUrl = `objects/versions?limit=none`;

  const stagesParam = stages?.map(stage => ObjectStageToRaw[stage]).join(",");
  const url = `${baseUrl}${stagesParam ? `&stage__in=${stagesParam}` : ""}`;

  return APIFetchStudio<SDOSavePointStatusResponse[]>(url, requestOptions).then(
    sdoSavePointStatusesModelCreator
  );
};

// Studio SDO Deployment

export const deployStudioDataObjectWithoutPayload = async ({
  versionId
}: {
  versionId: number;
}) => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio<SDODeploymentPayloadResponse>(
    `objects/deploy/${versionId}/initiate-deployment/`,
    requestOptions
  ).then(sdoDeploymentModelCreator);
};

export const deployStudioDataObjectWithPayload = async ({
  versionId,
  payload
}: {
  versionId: number;
  payload: { deployableJSON: ObjectTypeRaw[] };
}) => {
  const bodyRaw = {
    body: JSON.stringify({
      deployable_json: payload.deployableJSON
    })
  };

  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include",
    ...bodyRaw
  };

  return APIFetchStudio<SDODeploymentPayloadResponse>(
    `objects/deploy/${versionId}/initiate-deployment/`,
    requestOptions
  ).then(sdoDeploymentModelCreator);
};

// Studio SDO Object List Draft Locking
export const lockObjectListDraft = (): Promise<SDOObjectLock> => {
  const requestOptions: RequestInit = {
    method: "POST",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio<SDOObjectLockResponse>(
    `objects/lock/`,
    requestOptions
  ).then(sdoObjectLockModelCreator);
};

export const unlockObjectListDraft = (): Promise<void> => {
  const requestOptions: RequestInit = {
    method: "DELETE",
    headers: RequestHeaders.DELETE,
    credentials: "include"
  };

  return APIFetchStudio<void>(`objects/lock/`, requestOptions);
};

export const getSDObjectListLockStatus = (): Promise<SDOObjectLock> => {
  const requestOptions: RequestInit = {
    method: "GET",
    headers: RequestHeaders.POST,
    credentials: "include"
  };

  return APIFetchStudio<SDOObjectLockResponse>(
    `objects/lock/`,
    requestOptions
  ).then(sdoObjectLockModelCreator);
};
